import React, { useEffect } from 'react';
import { CELEBRATION_DURATION_MS, CelebrationType } from '../celebrationUtils';

const PARTICLE_COUNT = 28;
const RAIN_COUNT = 18;
const FIREWORK_COUNT = 24;

const celebrationLabels = {
    [CelebrationType.CONFETTI]: 'Confetti explosion',
    [CelebrationType.FIREWORKS]: 'Fireworks',
    [CelebrationType.STARS]: 'Floating stars',
    [CelebrationType.TROPHY]: 'Giant trophy',
    [CelebrationType.VOLLEYBALL_RAIN]: 'Volleyball rain',
    [CelebrationType.PARTY_POPPERS]: 'Party poppers'
};

function ConfettiExplosion() {
    return (
        <div className="celebration-effect confetti-effect" aria-hidden="true">
            {Array.from({ length: PARTICLE_COUNT }, (_, index) => (
                <span
                    key={index}
                    className="celebration-particle confetti-piece"
                    style={{
                        '--particle-index': index,
                        '--particle-angle': `${(360 / PARTICLE_COUNT) * index}deg`,
                        '--particle-delay': `${(index % 7) * 28}ms`
                    }}
                />
            ))}
        </div>
    );
}

function Fireworks() {
    return (
        <div className="celebration-effect fireworks-effect" aria-hidden="true">
            {Array.from({ length: FIREWORK_COUNT }, (_, index) => (
                <span
                    key={index}
                    className="celebration-particle firework-spark"
                    style={{
                        '--particle-index': index,
                        '--particle-angle': `${(360 / FIREWORK_COUNT) * index}deg`,
                        '--particle-delay': `${(index % 6) * 36}ms`
                    }}
                />
            ))}
        </div>
    );
}

function FloatingStars() {
    return (
        <div className="celebration-effect stars-effect" aria-hidden="true">
            {Array.from({ length: 20 }, (_, index) => (
                <span
                    key={index}
                    className="celebration-particle floating-star"
                    style={{
                        '--particle-index': index,
                        '--particle-left': `${(index * 19) % 100}%`,
                        '--particle-delay': `${(index % 8) * 120}ms`
                    }}
                >
                    ★
                </span>
            ))}
        </div>
    );
}

function GiantTrophy() {
    return (
        <div className="celebration-effect trophy-effect" aria-hidden="true">
            <div className="trophy-rays" />
            <div className="giant-trophy">🏆</div>
        </div>
    );
}

function VolleyballRain() {
    return (
        <div className="celebration-effect volleyball-rain-effect" aria-hidden="true">
            {Array.from({ length: RAIN_COUNT }, (_, index) => (
                <span
                    key={index}
                    className="celebration-particle volleyball-drop"
                    style={{
                        '--particle-index': index,
                        '--particle-left': `${(index * 23) % 100}%`,
                        '--particle-delay': `${(index % 9) * 110}ms`
                    }}
                >
                    🏐
                </span>
            ))}
        </div>
    );
}

function PartyPoppers() {
    return (
        <div className="celebration-effect party-poppers-effect" aria-hidden="true">
            <div className="party-popper left">🎉</div>
            <div className="party-popper right">🎉</div>
            {Array.from({ length: PARTICLE_COUNT }, (_, index) => (
                <span
                    key={index}
                    className="celebration-particle party-streamer"
                    style={{
                        '--particle-index': index,
                        '--particle-angle': `${(360 / PARTICLE_COUNT) * index}deg`,
                        '--particle-delay': `${(index % 7) * 34}ms`
                    }}
                />
            ))}
        </div>
    );
}

function CelebrationEffect({ type }) {
    switch (type) {
        case CelebrationType.FIREWORKS:
            return <Fireworks />;
        case CelebrationType.STARS:
            return <FloatingStars />;
        case CelebrationType.TROPHY:
            return <GiantTrophy />;
        case CelebrationType.VOLLEYBALL_RAIN:
            return <VolleyballRain />;
        case CelebrationType.PARTY_POPPERS:
            return <PartyPoppers />;
        case CelebrationType.CONFETTI:
        default:
            return <ConfettiExplosion />;
    }
}

export default function CelebrationOverlay({
    celebration,
    onClose,
    durationMs = CELEBRATION_DURATION_MS
}) {
    useEffect(() => {
        const timeoutId = window.setTimeout(onClose, durationMs);
        return () => window.clearTimeout(timeoutId);
    }, [durationMs, onClose, celebration.id]);

    const label = celebrationLabels[celebration.type] || 'Celebration';
    const messageSizeClass = celebration.message.length >= 20
        ? 'celebration-message-long'
        : celebration.message.length >= 13
            ? 'celebration-message-medium'
            : 'celebration-message-short';

    return (
        <div
            className={`celebration-overlay tint-${celebration.tint} celebration-${celebration.type}`}
            role="dialog"
            aria-modal="true"
            aria-label={`${label}: ${celebration.message}`}
        >
            <CelebrationEffect type={celebration.type} />
            <div className="celebration-content">
                <strong className={messageSizeClass}>{celebration.message}</strong>
            </div>
            <button
                type="button"
                className="celebration-dismiss"
                onClick={onClose}
                aria-label="Dismiss celebration"
            >
                ×
            </button>
        </div>
    );
}
